import { __ } from '@wordpress/i18n';

import { registerPlugin } from '@wordpress/plugins';
import { PluginDocumentSettingPanel, store as editorStore } from '@wordpress/editor';
import { Button, Spinner } from '@wordpress/components';
import { MediaUpload, MediaUploadCheck, useBlockProps } from '@wordpress/block-editor';
import { useEntityProp } from '@wordpress/core-data';
import { useSelect } from '@wordpress/data';
import { createElement, Fragment } from '@wordpress/element';

import {
	InspectorControls
} from '@wordpress/block-editor';
import {
	PanelBody,
	ToggleControl,
	__experimentalUnitControl as UnitControl
} from '@wordpress/components';

const META_KEY_FULL = '_featured_video_full_id';
const META_KEY_SHORT = '_featured_video_clip_id';
const META_KEY_LEGACY = '_featured_video_id';
const PLACEHOLDER_SRC = 'http://gonna-studios.local/wp-content/plugins/featured-media/placeholder.png';

function VideoSelector( {
	label,
	description,
	value,
	media,
	isResolving,
	isMissing,
	missingMessage,
	emptyMessage,
	replaceLabel,
	buttonLabel,
	onSelect,
	onClear,
} ) {
	const selectButtonLabel = buttonLabel || __( 'Select video', 'featured-media' );

	return (
		<div className="fmv-panel-section">
			<h3 className="fmv-panel-section__title">{ label }</h3>
			{ description ? (
				<p className="fmv-panel-section__description">{ description }</p>
			) : null }
			<MediaUploadCheck>
				<MediaUpload
					onSelect={ ( mediaObj ) => {
						if ( mediaObj?.type === 'video' ) {
							onSelect( mediaObj.id );
						}
					} }
					allowedTypes={ [ 'video' ] }
					value={ value || undefined }
					render={ ( { open } ) => (
						<Fragment>
							{ isResolving ? (
								<div className="fmv-spinner">
									<Spinner />
								</div>
							) : value && isMissing ? (
								<div className="fmv-empty fmv-empty--warning">
									<p>{ missingMessage || __( 'The selected video could not be found. Please choose another file.', 'featured-media' ) }</p>
									<div className="fmv-actions">
										<Button variant="primary" onClick={ open }>
											{ replaceLabel || __( 'Select video', 'featured-media' ) }
										</Button>
										<Button variant="secondary" onClick={ onClear }>
											{ __( 'Remove', 'featured-media' ) }
										</Button>
									</div>
								</div>
							) : value && media?.source_url ? (
								<div className="fmv-preview">
									<video controls src={ media.source_url } />
									<div className="fmv-actions">
										<Button variant="primary" onClick={ open }>
											{ replaceLabel }
										</Button>
										<Button variant="secondary" onClick={ onClear }>
											{ __( 'Remove', 'featured-media' ) }
										</Button>
									</div>
								</div>
							) : (
								<div className="fmv-empty">
									{ emptyMessage ? (
										<p>{ emptyMessage }</p>
									) : null }
									<Button variant="primary" onClick={ open }>
										{ selectButtonLabel }
									</Button>
								</div>
							) }
						</Fragment>
					) }
				/>
			</MediaUploadCheck>
		</div>
	);
}

function FeaturedMediaPanel() {
	// Current post type + meta
	const postType = useSelect( ( select ) => select( 'core/editor' ).getCurrentPostType(), [] );
	const [ meta, setMeta ] = useEntityProp( 'postType', postType, 'meta' );

	const fullVideoId = meta?.[ META_KEY_FULL ] || meta?.[ META_KEY_LEGACY ] || 0;
	const clipVideoId = meta?.[ META_KEY_SHORT ] || 0;

	const {
		clipMedia,
		clipResolving,
		clipMissing,
		fullMedia,
		fullResolving,
		fullMissing,
	} = useSelect(
		( select ) => {
			const coreStore = select( 'core' );
			const dataStore = select( 'core/data' );

			const computeState = ( id ) => {
				if ( ! id ) {
					return {
						media: null,
						resolving: false,
						missing: false,
					};
				}

				const record = coreStore.getMedia( id );
				const hasResolved = dataStore.hasFinishedResolution( 'core', 'getMedia', [ id ] );
				const hasSource = !! record?.source_url;

				return {
					media: hasSource ? record : null,
					resolving: ! hasResolved && ! hasSource,
					missing: hasResolved && ! hasSource,
				};
			};

			const clipState = computeState( clipVideoId );
			const fullState = computeState( fullVideoId );

			return {
				clipMedia: clipState.media,
				clipResolving: clipState.resolving,
				clipMissing: clipState.missing,
				fullMedia: fullState.media,
				fullResolving: fullState.resolving,
				fullMissing: fullState.missing,
			};
		},
		[ clipVideoId, fullVideoId ]
	);

	const setClipVideo = ( id ) => {
		const nextMeta = { ...( meta || {} ) };
		nextMeta[ META_KEY_SHORT ] = id || 0;
		setMeta( nextMeta );
	};

	const setFullVideo = ( id ) => {
		const nextMeta = { ...( meta || {} ) };
		const safeId = id || 0;
		nextMeta[ META_KEY_FULL ] = safeId;
		nextMeta[ META_KEY_LEGACY ] = safeId;
		setMeta( nextMeta );
	};

	return (
		<PluginDocumentSettingPanel
			name="featured-media-video"
			title={ __( 'Featured Media (Video)', 'featured-media' ) }
			className="featured-media-video-panel"
			initialOpen={ true }
		>
			<VideoSelector
				label={ __( 'Short clip', 'featured-media' ) }
				description={ __( 'Shown by default on the frontend.', 'featured-media' ) }
				value={ clipVideoId }
				media={ clipMedia }
				isResolving={ clipResolving }
				isMissing={ clipMissing }
				missingMessage={ __( 'The short clip appears to be missing. Please choose a new video.', 'featured-media' ) }
				onSelect={ setClipVideo }
				onClear={ () => setClipVideo( 0 ) }
				emptyMessage={ __( 'Select or upload a short teaser video.', 'featured-media' ) }
				buttonLabel={ __( 'Choose short clip', 'featured-media' ) }
				replaceLabel={ __( 'Replace short clip', 'featured-media' ) }
			/>
			<VideoSelector
				label={ __( 'Full video', 'featured-media' ) }
				description={ __( 'Shown after the teaser is clicked.', 'featured-media' ) }
				value={ fullVideoId }
				media={ fullMedia }
				isResolving={ fullResolving }
				isMissing={ fullMissing }
				missingMessage={ __( 'The full video appears to be missing. Please choose a new video.', 'featured-media' ) }
				onSelect={ setFullVideo }
				onClear={ () => setFullVideo( 0 ) }
				emptyMessage={ __( 'Select or upload the full-length video.', 'featured-media' ) }
				buttonLabel={ __( 'Choose full video', 'featured-media' ) }
				replaceLabel={ __( 'Replace full video', 'featured-media' ) }
			/>
		</PluginDocumentSettingPanel>
	);
}

registerPlugin( 'featured-media-video', {
	render: FeaturedMediaPanel,
	icon: null,
} );

export default function Edit( { attributes, setAttributes, clientId, context } ) {
	const { autoplay, loop, muted, controls, playsInline, maxHeight, startWithFull } = attributes;

	const blockProps = useBlockProps();

	// 1) Prefer explicit context from Query Loop
	const ctxPostId = context && typeof context.postId !== 'undefined' ? context.postId : undefined;
	const ctxPostType = context && typeof context.postType !== 'undefined' ? context.postType : undefined;

	// 2) Fallback to editor document
	const { editorPostId, editorPostType } = useSelect(
		( select ) => {
			const editor = select( editorStore );
			return {
				editorPostId: editor?.getCurrentPostId ? editor.getCurrentPostId() : undefined,
				editorPostType: editor?.getCurrentPostType ? editor.getCurrentPostType() : undefined,
			};
		},
		[]
	);

	const targetPostId = ( typeof ctxPostId !== 'undefined' ? ctxPostId : editorPostId ) || 0;
	const targetPostType = ( typeof ctxPostType !== 'undefined' ? ctxPostType : editorPostType ) || 'post';

	const [ meta ] = useEntityProp(
		'postType',
		targetPostType,
		'meta',
		targetPostId
	);

	const clipMetaId = meta?.[ META_KEY_SHORT ] || 0;
	const fullMetaId = meta?.[ META_KEY_FULL ] || meta?.[ META_KEY_LEGACY ] || 0;

	const {
		clipVideo,
		clipResolving,
		clipMissing,
		fullVideo,
		fullResolving,
		fullMissing,
	} = useSelect(
		( select ) => {
			const coreStore = select( 'core' );
			const dataStore = select( 'core/data' );

			const computeState = ( id ) => {
				if ( ! id ) {
					return {
						media: null,
						resolving: false,
						missing: false,
					};
				}

				const record = coreStore.getMedia( id );
				const hasResolved = dataStore.hasFinishedResolution( 'core', 'getMedia', [ id ] );
				const hasSource = !! record?.source_url;

				return {
					media: hasSource ? record : null,
					resolving: ! hasResolved && ! hasSource,
					missing: hasResolved && ! hasSource,
				};
			};

			const clipState = computeState( clipMetaId );
			const fullState = computeState( fullMetaId );

			return {
				clipVideo: clipState.media,
				clipResolving: clipState.resolving,
				clipMissing: clipState.missing,
				fullVideo: fullState.media,
				fullResolving: fullState.resolving,
				fullMissing: fullState.missing,
			};
		},
		[ clipMetaId, fullMetaId ]
	);

	const [ featuredMediaId ] = useEntityProp(
		'postType',
		targetPostType,
		'featured_media',
		targetPostId
	);
	const featured = useSelect(
		( select ) => featuredMediaId ? select( 'core' ).getMedia( featuredMediaId ) : null,
		[ featuredMediaId ]
	);

	const previewVideo = ( startWithFull && fullVideo ) ? fullVideo : ( clipVideo || fullVideo );
	const previewUsesClip = ! startWithFull && !! clipVideo;
	const hasFullVideo = !! fullVideo;
	const hasFeaturedImage = !! featured?.source_url;
	const isResolvingPreview = ( !! clipMetaId && clipResolving ) || ( !! fullMetaId && fullResolving );
	const missingMessages = [];

	if ( clipMissing ) {
		missingMessages.push( __( 'The short clip is missing. Replace it in the sidebar.', 'featured-media' ) );
	}
	if ( fullMissing ) {
		missingMessages.push( __( 'The full video is missing. Replace it in the sidebar.', 'featured-media' ) );
	}

	const hasMissing = missingMessages.length > 0;
	const hasAnyVideoSelection = !! clipMetaId || !! fullMetaId;

	if ( isResolvingPreview ) {
		return (
			<div { ...blockProps } className={(blockProps.className || '') + ' featured-video-block'}>
				<Spinner />
			</div>
		);
	}

	const frameStyle = maxHeight ? { maxHeight } : undefined;

	if ( previewVideo?.source_url ) {
		return (
			<>
				<InspectorControls>
					<PanelBody title="Video Settings">
						<ToggleControl label="Autoplay" checked={ autoplay } onChange={ (val) => setAttributes({ autoplay: val }) } />
						<ToggleControl label="Loop" checked={ loop } onChange={ (val) => setAttributes({ loop: val }) } />
						<ToggleControl label="Muted" checked={ muted } onChange={ (val) => setAttributes({ muted: val }) } />
						<ToggleControl label="Show Playback Controls" checked={ controls } onChange={ (val) => setAttributes({ controls: val }) } />
						<ToggleControl label="Play Inline" checked={ playsInline } onChange={ (val) => setAttributes({ playsInline: val }) } />
						<ToggleControl
							label={ __( 'Start with full video', 'featured-media' ) }
							checked={ !! startWithFull }
							onChange={ ( val ) => setAttributes( { startWithFull: val } ) }
							help={ __( 'Show the featured image with a play button that plays the full video.', 'featured-media' ) }
						/>
                        
					</PanelBody>
				</InspectorControls>
				<div { ...blockProps } className={(blockProps.className || '') + ' featured-video-block'}>
					<div className="featured-video__frame" style={ frameStyle }>
						<video
							src={ previewVideo.source_url }
							style={{ pointerEvents: 'none' }}
							autoPlay={ autoplay }
							loop={ loop }
							muted={ muted }
							controls={ previewUsesClip && hasFullVideo ? false : controls }
							playsInline={ playsInline }
						/>

						{ startWithFull && hasFullVideo ? (
							<div className="featured-video__editor-note">
								{ hasFeaturedImage
									? __( 'Frontend shows the featured image until the full video is played.', 'featured-media' )
									: __( 'Frontend starts with the full video.', 'featured-media' )
								}
							</div>
						) : null }
					</div>
					{ hasMissing ? (
						<div className="featured-video__editor-warning">
							{ missingMessages.map( ( message, index ) => (
								<p key={ index }>{ message }</p>
							) ) }
						</div>
					) : null }
				</div>
			</>
		);
	}

	return (
		<>
			<InspectorControls>
				<PanelBody title="Video Settings">
					<ToggleControl label="Autoplay" checked={ autoplay } onChange={ (val) => setAttributes({ autoplay: val }) } />
					<ToggleControl label="Loop" checked={ loop } onChange={ (val) => setAttributes({ loop: val }) } />
					<ToggleControl label="Muted" checked={ muted } onChange={ (val) => setAttributes({ muted: val }) } />
					<ToggleControl label="Show Playback Controls" checked={ controls } onChange={ (val) => setAttributes({ controls: val }) } />
					<ToggleControl label="Play Inline" checked={ playsInline } onChange={ (val) => setAttributes({ playsInline: val }) } />
					<ToggleControl
						label={ __( 'Start with full video', 'featured-media' ) }
						checked={ !! startWithFull }
						onChange={ ( val ) => setAttributes( { startWithFull: val } ) }
						help={ __( 'Show the featured image with a play button that plays the full video.', 'featured-media' ) }
					/>
				</PanelBody>
			</InspectorControls>
			<div { ...blockProps } className={(blockProps.className || '') + ' featured-video-block'}>
				<div className="featured-video__frame" style={ frameStyle }>
					<img
						src={ featured?.source_url ? featured.source_url : PLACEHOLDER_SRC }
						alt=""
						className={ featured?.source_url ? '' : 'featured-video__placeholder' }
						style={{ display: 'block' }}
						decoding="async"
					/>
				</div>
				{ hasMissing ? (
					<div className="featured-video__editor-warning">
						{ missingMessages.map( ( message, index ) => (
							<p key={ index }>{ message }</p>
						) ) }
					</div>
				) : null }
				
			</div>
		</>
	);
}
